import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaTrash, FaSave, FaSearch } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import userService from '../../services/userService';
import AdminSettingsBackLink from '../../components/admin/AdminSettingsBackLink';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Panel = styled.section`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-sm);
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  max-width: 360px;
  min-width: 0;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.9rem;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.25);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid var(--color-border-primary);
    vertical-align: middle;
  }

  th {
    color: var(--color-text-secondary);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  tbody tr:hover {
    background: var(--color-hover-overlay);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.75rem;
  border: 1px solid ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-border-primary)'
        : 'var(--color-btn-primary-border)'};
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $variant }) =>
    $variant === 'danger'
      ? 'transparent'
      : $variant === 'secondary'
        ? 'var(--color-bg-tertiary)'
        : 'var(--color-btn-primary-bg)'};
  color: ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-text-primary)'
        : 'var(--color-btn-primary-text)'};
  box-sizing: border-box;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Badge = styled.span`
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-primary);
`;

const UsersAdminPage = () => {
  const { reloadUsers, addToast, currentUser } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await userService.getAdminUsers();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      addToast?.('Erro ao carregar usuários.', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = async () => {
    await load();
    await reloadUsers?.();
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const name = String(row.name || '').toLowerCase();
      const email = String(row.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [items, search]);

  const handleToggleAssignable = async (row) => {
    setSavingId(row.id);
    try {
      await userService.updateAssignable(row.id, !row.assignable);
      addToast?.('Preferência de atribuição atualizada.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao atualizar usuário.';
      addToast?.(msg, 'error');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (row) => {
    if (currentUser?.id === row.id) {
      addToast?.('Você não pode remover o seu próprio usuário.', 'error');
      return;
    }
    if (!window.confirm(`Remover o usuário "${row.name || row.email}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await userService.delete(row.id);
      addToast?.('Usuário removido.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Não foi possível remover o usuário.';
      addToast?.(msg, 'error');
    }
  };

  return (
    <PageContainer>
      <AdminSettingsBackLink />
      <Panel>
        <PageTitle>Usuários do SupportHub</PageTitle>
        <Hint>
          Habilite quem pode aparecer nas listas de responsáveis e filtros. Usuários com histórico em
          tickets não podem ser apagados — desative a atribuição.
        </Hint>

        <SearchRow>
          <FaSearch color="var(--color-text-secondary)" />
          <SearchInput
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail..."
          />
        </SearchRow>

        {loading ? (
          <p>Carregando...</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Atribuível</th>
                  <th>Tickets como responsável</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {row.name || '—'}
                      {currentUser?.id === row.id && <Badge>você</Badge>}
                    </td>
                    <td>{row.email || '—'}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!row.assignable}
                        disabled={savingId === row.id}
                        onChange={() => handleToggleAssignable(row)}
                        style={{ width: 18, height: 18, accentColor: '#28a745' }}
                      />
                      {!row.assignable && <Badge>oculto</Badge>}
                    </td>
                    <td>{row.responsibleTicketCount ?? 0}</td>
                    <td style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        type="button"
                        $variant="secondary"
                        onClick={() => handleToggleAssignable(row)}
                        disabled={savingId === row.id}
                      >
                        <FaSave />
                        {row.assignable ? 'Desabilitar' : 'Habilitar'}
                      </Button>
                      <Button
                        type="button"
                        $variant="danger"
                        onClick={() => handleDelete(row)}
                        disabled={currentUser?.id === row.id}
                      >
                        <FaTrash />
                        Apagar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>
    </PageContainer>
  );
};

export default UsersAdminPage;
